import * as React from "react";
import {useEffect, useState, useRef} from "react";
import Paper from "@material-ui/core/Paper";
import {EditingState, ViewState} from "@devexpress/dx-react-scheduler";
import PropTypes from "prop-types";
import moment from "moment";
import {
    AppointmentForm,
    Appointments,
    AppointmentTooltip,
    DateNavigator,
    DragDropProvider,
    EditRecurrenceMenu,
    MonthView,
    Scheduler,
    TodayButton,
    Toolbar,
} from "@devexpress/dx-react-scheduler-material-ui";
import {connectProps} from "@devexpress/dx-react-core";
import AppointmentFormContainer from "./CalendarForm";
import "./Calendar.sass";
import CalendarAppointment from "./CalendarAppointment";
import {
    darken,
    fade,
    lighten,
} from "@material-ui/core/styles/colorManipulator";
import {TableCell} from "@material-ui/core";
import classNames from "clsx";
import {withStyles} from "@material-ui/core/styles";
import CalendarAppointmentContainer from './CalendarAppointmentContainer'

const todayBtnLocale = {
    today: "Сегодня",
};
const getBorder = (theme) =>
    `1px solid ${
        theme.palette.type === "light"
            ? lighten(fade(theme.palette.divider, 1), 0.88)
            : darken(fade(theme.palette.divider, 1), 0.68)
    }`;

let Calendar = ({
                    showOnCalendar,
                    onItemDelete,
                    addItem,
                    editItem,
                    // cellHeight,
                    currentDate,
                    actualOrg,
                    calendarFormConf,
                }) => {
    const data = showOnCalendar;
    const [currentTime, setCurrentTime] = useState(currentDate);
    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const [deletedAppointmentId, setDeletedAppointmentId] = useState(undefined);
    const [deletedEditingAppointment, setDeletedEditingAppointment] = useState(
        undefined
    );
    const [previousAppointment, setPreviousAppointment] = useState(undefined);
    const [editingFormVisible, setEditingFormVisible] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(false);
    const [addedAppointment, setAddedAppointment] = useState({});
    const [isNewAppointment, setIsNewAppointment] = useState(false);
    const [cellHeight, setCellHeight] = useState();

    const CellBase = React.memo(
        ({classes, startDate, formatDate, otherMonth}) => {
            const isFirstMonthDay = startDate.getDate() === 1;
            const formatOptions = isFirstMonthDay
                ? {day: "numeric", month: "long"}
                : {day: "numeric"};
            return (
                <TableCell
                    tabIndex={0}
                    className={classNames({[classes.cell]: true})}
                    onClick={() => {
                        toggleEditingFormVisibility();
                        setEditingAppointment(undefined);
                        setAddedAppointment({
                            startDate: moment(startDate),
                            endDate: moment(startDate).add(1, "h"),
                            orgName: actualOrg ? actualOrg.orgName : "",
                            organizationId: actualOrg ? actualOrg.organizationId : "",
                            verCenterName: actualOrg.verCenterName
                                ? actualOrg.verCenterName
                                : "",
                            verificationId: actualOrg.verCenterId
                                ? actualOrg.verCenterId
                                : "",
                            medName: actualOrg.medName ? actualOrg.medName : "",
                            medId: actualOrg.medId ? actualOrg.medId : "",
                        });
                    }}
                >
                    <div className={classes.text}>
                        {formatDate(startDate, formatOptions)}
                    </div>
                </TableCell>
            );
        }
    );
    const styles = (theme) => ({
        cell: {
            color: "#78909C !important",
            position: "relative",
            userSelect: "none",
            verticalAlign: "top",
            padding: 5,
            borderLeft: getBorder(theme),
            textAlign: "center",
            fontSize: "12px",
            height: cellHeight + 'px',
            paddingRight: '5px',
            "&:first-child": {
                borderLeft: "none",
            },
            "&:last-child": {
                paddingRight: 0,
            },
            "tr:last-child &": {
                borderBottom: "none",
            },
            "&:hover": {
                backgroundColor: "white",
            },
            "&:focus": {
                backgroundColor: fade(theme.palette.primary.main, 0.15),
                outline: 0,
            },
        },
    });

    const CalendarForm = connectProps(AppointmentFormContainer, () => {
        const currentAppointment =
            data.filter(
                (appointment) =>
                    editingAppointment && appointment.id === editingAppointment.id
            )[0] || addedAppointment;
        const cancelAppointment = () => {
            if (isNewAppointment) {
                setEditingAppointment(previousAppointment);
                setIsNewAppointment(false);
            }
        };

        return {
            visible: editingFormVisible,
            addedAppointment,
            appointmentData: currentAppointment,
            commitChanges: commitChanges,
            visibleChange: toggleEditingFormVisibility,
            onEditingAppointmentChange: setEditingAppointment,
            cancelAppointment,
            calendarFormConf,
        };
    });

    function onAddedAppointmentChange(addedAppointment) {
        setAddedAppointment(addedAppointment);
        if (editingAppointment !== undefined) {
            setPreviousAppointment(editingAppointment);
        }
        setEditingAppointment(undefined);
        setIsNewAppointment(true);
    }

    function toggleEditingFormVisibility() {
        setEditingFormVisible(!editingFormVisible);
    }

    function toggleConfirmationVisible() {
        setConfirmationVisible(!confirmationVisible);
    }

    function commitDeletedAppointment() {
        onItemDelete(deletedAppointmentId);
    }

    async function commitChanges({added, changed, deleted}) {
        if (added) {
            await addItem(added);
        }
        if (changed) {
            let formData = {};
            data.forEach((appointment) =>
                changed[appointment.id]
                    ? (formData = {...appointment, ...changed[appointment.id]})
                    : appointment
            );
            await editItem(formData);
        }
        if (deleted) {
            if (deleted !== undefined) {
                // setDeletedAppointmentId(deleted);
                onItemDelete(deleted);
                // toggleConfirmationVisible();
            }
        }
        return {data, addedAppointment: {}};
    }

    useEffect(() => {
        CalendarForm.update();
    }, []);

    const Cell = withStyles(styles, {name: "Cell"})(CellBase);
    const def = useRef();

    useEffect(() => {
        setCellHeight(def.current.offsetHeight / 6.9);

    }, [def]);
    return (
        <>
            <div ref={def} className={"calendar"}>

                <Scheduler firstDayOfWeek={1} locale={"ru-RU"} data={data}>
                    <ViewState
                        currentDate={currentTime}
                        onCurrentDateChange={setCurrentTime}
                        onCurrentDateChange={setCurrentTime}
                    />
                    <EditingState
                        onCommitChanges={commitChanges}
                        onEditingAppointmentChange={setEditingAppointment}
                        onAddedAppointmentChange={setAddedAppointment}
                    />

                    <MonthView intervalCount={1} timeTableCellComponent={Cell}/>
                    <EditRecurrenceMenu/>
                    <Appointments onClick={toggleEditingFormVisibility}     draggable={false} containerComponent={CalendarAppointmentContainer} appointmentComponent={CalendarAppointment}/>
                    <Toolbar/>
                    <DateNavigator/>
                    <TodayButton messages={todayBtnLocale}/>
                    <AppointmentForm
                        overlayComponent={CalendarForm}
                        visible={editingFormVisible}
                        onVisibilityChange={toggleEditingFormVisibility}
                    />
                </Scheduler>

            </div>
        </>
    );
};
Calendar = React.memo(Calendar);

export default Calendar;

Calendar.propTypes = {
    showOnCalendar: PropTypes.array,
    currentDate: PropTypes.any,
    cellHeight: PropTypes.number,
    editItem: PropTypes.func,
    addItem: PropTypes.func,
    onItemDelete: PropTypes.func,
};
Calendar.defaultProps = {
    showOnCalendar: [],
    onItemDelete: () => {
    },
    addItem: () => {
    },
    editItem: () => {
    },
    cellHeight: 115,
};
